import { useState } from 'react';
import { Calendar, Plus, AlertTriangle, ArrowRight, Upload, Download } from 'lucide-react';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { FiscalYearList } from '../../components/fiscal-years/FiscalYearList';
import { NewFiscalYearForm } from '../../components/fiscal-years/NewFiscalYearForm';
import { CloseFiscalYearModal } from '../../components/fiscal-years/CloseFiscalYearModal';
import { ReopenFiscalYearModal } from '../../components/fiscal-years/ReopenFiscalYearModal';
import { AuditFiscalYearModal } from '../../components/fiscal-years/AuditFiscalYearModal';
import { TransitionFiscalYearModal } from '../../components/fiscal-years/TransitionFiscalYearModal';
import { InitializeAccountsModal } from '../../components/fiscal-years/InitializeAccountsModal';
import { ImportDataModal } from '../../components/fiscal-years/ImportDataModal';
import { ExportDataModal } from '../../components/fiscal-years/ExportDataModal';
import { DeleteFiscalYearModal } from '../../components/fiscal-years/DeleteFiscalYearModal';
import { ImportStatusBadge } from '../../components/ui/ImportStatusBadge';
import { Modal } from '../../components/ui/Modal';
import { useFiscalYear } from '../../hooks/useFiscalYear';
import { useDataExchange } from '../../hooks/useDataExchange';
import type { FiscalYear } from '../../types/fiscal-year';
import { useFiscalYearAudit } from '../../hooks/useFiscalYearAudit';
import { usePermissions } from '../../hooks/usePermissions';
import { useAuth0 } from '@auth0/auth0-react';
import toast from 'react-hot-toast';

export function FiscalYearsPage() {
  const [showNewPeriodModal, setShowNewPeriodModal] = useState(false);
  const [selectedFiscalYear, setSelectedFiscalYear] = useState<FiscalYear | null>(null);
  const [showCloseModal, setShowCloseModal] = useState(false);
  const [showReopenModal, setShowReopenModal] = useState(false);
  const [showAuditModal, setShowAuditModal] = useState(false);
  const [showTransitionModal, setShowTransitionModal] = useState(false);
  const [showInitializeModal, setShowInitializeModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  
  const { 
    fiscalYears, 
    currentFiscalYear, 
    loading, 
    error,
    createFiscalYear, 
    reopenFiscalYear,
    closeFiscalYear,
    selectFiscalYear,
    loadFiscalYears
  } = useFiscalYear();
  
  const { validateAudit } = useFiscalYearAudit();
  const { activeTask, stopPolling } = useDataExchange();
  const { user } = useAuth0();
  const { isAdmin, isSuperAdmin } = usePermissions();

  const handleClose = (fiscalYear: FiscalYear) => {
    setSelectedFiscalYear(fiscalYear);
    setShowCloseModal(true);
  };

  const handleReopen = (fiscalYear: FiscalYear) => {
    setSelectedFiscalYear(fiscalYear);
    setShowReopenModal(true);
  };

  const handleAudit = (fiscalYear: FiscalYear) => {
    setSelectedFiscalYear(fiscalYear);
    setShowAuditModal(true);
  };

  const handleTransition = (fiscalYear: FiscalYear) => {
    setSelectedFiscalYear(fiscalYear);
    setShowTransitionModal(true);
  };

  const handleInitialize = (fiscalYear: FiscalYear) => {
    setSelectedFiscalYear(fiscalYear);
    setShowInitializeModal(true);
  };

  const handleImport = (fiscalYear: FiscalYear) => {
    setSelectedFiscalYear(fiscalYear);
    setShowImportModal(true);
  };

  const handleExport = (fiscalYear: FiscalYear) => {
    setSelectedFiscalYear(fiscalYear);
    setShowExportModal(true);
  };

  const handleDelete = (fiscalYear: FiscalYear) => {
    setSelectedFiscalYear(fiscalYear);
    setShowDeleteModal(true);
  };

  const handleDeleteSuccess = async () => {
    await loadFiscalYears();
    toast.success('Exercice fiscal supprimé avec succès');
  };

  const handleImportStarted = async (taskId: string) => {
    // Recharger les données après un import
    await loadFiscalYears();
  };

  const handleInitializeSuccess = async () => {
    await loadFiscalYears();
    // Don't close modal - let user see the result
  };

  const handleTransitionSuccess = async (newFiscalYearId: string) => {
    toast.success('Nouvel exercice créé avec succès !');
    await loadFiscalYears();
    setShowTransitionModal(false);
    setSelectedFiscalYear(null);
  };

  const handleCreateFiscalYear = async (data: { startDate: string; endDate: string; code: string }) => {
    try {
      const result = await createFiscalYear(data);
      if (result) {
        toast.success('Exercice fiscal créé avec succès');
        setShowNewPeriodModal(false);
      } else {
        toast.error('Erreur lors de la création de l\'exercice fiscal');
      }
    } catch (err) {
      console.error('Error creating fiscal year:', err);
      toast.error('Erreur lors de la création de l\'exercice fiscal');
    }
  };

  const handleCloseConfirm = async () => {
    if (!selectedFiscalYear) return;
    try {
      await closeFiscalYear(selectedFiscalYear.id);
      toast.success('Exercice fiscal clôturé avec succès');
      setShowCloseModal(false);
      setSelectedFiscalYear(null);
    } catch (err) {
      console.error('Error closing fiscal year:', err);
      toast.error('Erreur lors de la clôture de l\'exercice fiscal');
    }
  };

  const handleReopenConfirm = async () => {
    if (!selectedFiscalYear) return;
    try {
      await reopenFiscalYear(selectedFiscalYear.id);
      toast.success('Exercice fiscal réouvert avec succès');
      setShowReopenModal(false);
      setSelectedFiscalYear(null);
    } catch (err) {
      console.error('Error reopening fiscal year:', err);
      toast.error('Erreur lors de la réouverture de l\'exercice fiscal');
    }
  };

  const handleAuditConfirm = async (credentials: any) => {
    if (!selectedFiscalYear) return;
    const validation = await validateAudit(selectedFiscalYear, credentials);
    if (validation.success) {
      // Mettre à jour le statut d'audit
      selectFiscalYear({
        ...selectedFiscalYear,
        auditStatus: {
          isAudited: true,
          auditor: {
            name: credentials.name,
            registrationNumber: credentials.registrationNumber
          },
          auditedAt: new Date().toISOString()
        }
      });
      toast.success('Exercice fiscal audité avec succès');
      // Recharger les données
      await loadFiscalYears();
    } else {
      throw new Error(validation.message);
    }
  };

  const roles = user?.[`${import.meta.env.VITE_AUTH0_AUDIENCE}/roles`] as string[] | undefined;
  const isAuditor = roles?.includes('auditor');

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Exercices Comptables</h1>
        <div className="flex gap-2">
          {/* Bouton de transition si un exercice clôturé/audité existe */}
          {fiscalYears.some(fy => fy.status === 'closed' || fy.status === 'audited') && (
            <Button
              variant="secondary"
              icon={ArrowRight}
              onClick={() => {
                // Sélectionner le dernier exercice clôturé/audité
                const closedOrAudited = fiscalYears
                  .filter(fy => fy.status === 'closed' || fy.status === 'audited')
                  .sort((a, b) => new Date(b.endDate).getTime() - new Date(a.endDate).getTime());
                if (closedOrAudited.length > 0) {
                  handleTransition(closedOrAudited[0]);
                }
              }}
            >
              Nouvel exercice avec report
            </Button>
          )}
          <Button
            variant="primary"
            icon={Plus}
            onClick={() => setShowNewPeriodModal(true)}
          >
            Nouvel Exercice
          </Button>
        </div>
      </div>

      {/* Message d'erreur */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <p className="text-red-600 dark:text-red-400">{error}</p>
          <Button 
            variant="secondary" 
            onClick={() => loadFiscalYears()}
            className="mt-2"
          >
            Réessayer
          </Button>
        </div>
      )}

      {/* Alerte si aucun exercice fiscal n'est ouvert (mais des exercices existent) */}
      {!loading && fiscalYears.length > 0 && !fiscalYears.some(fy => fy.status === 'open') && (
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
          <div className="flex items-center text-yellow-600 dark:text-yellow-400">
            <AlertTriangle className="h-5 w-5 mr-2 flex-shrink-0" />
            <p>Aucun exercice fiscal ouvert. Veuillez créer ou réouvrir un exercice pour pouvoir saisir des écritures.</p>
          </div>
        </div>
      )}

      {/* Indicateur d'import discret (visible seulement si un import est en cours) */}
      {activeTask && !['completed', 'failed', 'cancelled'].includes(activeTask.status) && (
        <ImportStatusBadge
          status={activeTask.status}
          progress={activeTask.progress}
          currentStep={activeTask.currentStep}
          processedLines={activeTask.processedLines}
          totalLines={activeTask.totalLines}
          errorCount={activeTask.errorCount}
          compact={false}
        />
      )}

      <Card title="Liste des exercices" icon={Calendar}>
        <FiscalYearList
          fiscalYears={fiscalYears}
          onClose={handleClose}
          onReopen={handleReopen}
          onAudit={handleAudit}
          onInitialize={handleInitialize}
          onImport={handleImport}
          onExport={handleExport}
          onDelete={isAdmin ? handleDelete : undefined}
          selectedFiscalYear={currentFiscalYear}
          loading={loading}
          isAuditor={isAuditor}
          isAdmin={isAdmin}
        />
      </Card>

      <Modal title="Nouvel Exercice Comptable" isOpen={showNewPeriodModal} onClose={() => setShowNewPeriodModal(false)}>
        <NewFiscalYearForm
          onSubmit={handleCreateFiscalYear}
          onCancel={() => setShowNewPeriodModal(false)}
          onImport={async (data) => {
            // Si un exercice ouvert existe, ouvrir le modal d'import
            const openFy = fiscalYears.find(fy => fy.status === 'open');
            if (openFy) {
              setSelectedFiscalYear(openFy);
              setShowNewPeriodModal(false);
              setShowImportModal(true);
            } else {
              toast.info('Créez d\'abord un exercice fiscal pour importer des données');
            }
          }}
        />
      </Modal>

      {selectedFiscalYear && (
        <>
          {showCloseModal && (
            <CloseFiscalYearModal
              isOpen={true}
              onClose={() => {
                setShowCloseModal(false);
                setSelectedFiscalYear(null);
              }}
              fiscalYear={selectedFiscalYear}
            />
          )}

          {showReopenModal && (
            <ReopenFiscalYearModal
              isOpen={true}
              onClose={() => {
                setShowReopenModal(false);
                setSelectedFiscalYear(null);
              }}
              fiscalYear={selectedFiscalYear}
              onConfirm={handleReopenConfirm}
            />
          )}

          {showAuditModal && (
            <AuditFiscalYearModal
              isOpen={true}
              onClose={() => {
                setShowAuditModal(false);
                setSelectedFiscalYear(null);
              }}
              fiscalYear={selectedFiscalYear}
              onConfirm={handleAuditConfirm}
            />
          )}

          {showTransitionModal && (
            <TransitionFiscalYearModal
              isOpen={true}
              onClose={() => {
                setShowTransitionModal(false);
                setSelectedFiscalYear(null);
              }}
              previousFiscalYear={selectedFiscalYear}
              onSuccess={handleTransitionSuccess}
            />
          )}

          {showInitializeModal && (
            <InitializeAccountsModal
              isOpen={true}
              onClose={() => {
                setShowInitializeModal(false);
                setSelectedFiscalYear(null);
              }}
              fiscalYear={selectedFiscalYear}
              onSuccess={handleInitializeSuccess}
            />
          )}

          {showImportModal && (
            <ImportDataModal
              isOpen={true}
              onClose={() => {
                setShowImportModal(false);
                setSelectedFiscalYear(null);
              }}
              fiscalYear={selectedFiscalYear}
              onImportStarted={handleImportStarted}
            />
          )}

          {showExportModal && (
            <ExportDataModal
              isOpen={true}
              onClose={() => {
                setShowExportModal(false);
                setSelectedFiscalYear(null);
              }}
              fiscalYear={selectedFiscalYear}
            />
          )}

          {showDeleteModal && (
            <DeleteFiscalYearModal
              isOpen={true}
              onClose={() => {
                setShowDeleteModal(false);
                setSelectedFiscalYear(null);
              }}
              fiscalYear={selectedFiscalYear}
              isSuperAdmin={isSuperAdmin}
              onSuccess={handleDeleteSuccess}
            />
          )}
        </>
      )}
    </div>
  );
}